import { useState } from 'react';
import { v1, toResult } from '@/infrastructure/api/v1/client';

export interface GeneratedText {
  projectName?: string;
  projectDescription?: string;
  projectTags?: string[];
  sceneName?: string;
  sceneIntroduction?: string;
}

export interface GeneratedImages {
  portraitUrl?: string;
  landscapeUrl?: string;
}

export interface UsePublishAiAssist {
  isGeneratingText: boolean;
  isGeneratingImages: boolean;
  textError: string | null;
  imagesError: string | null;
  generateText: (input: { shortDescription: string; projectInstanceId?: string | null }) => Promise<GeneratedText>;
  generateImages: (input: { seedUrl: string; projectInstanceId?: string | null }) => Promise<GeneratedImages>;
}

const GENERIC_ERROR = 'Generation failed. Please try again.';

const COVERS_POLL_INTERVAL_MS = 2000;
const COVERS_POLL_TIMEOUT_MS = 3 * 60 * 1000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function usePublishAiAssist(): UsePublishAiAssist {
  const [isGeneratingText, setIsGeneratingText] = useState(false);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [textError, setTextError] = useState<string | null>(null);
  const [imagesError, setImagesError] = useState<string | null>(null);

  // The backend spends on generation only for a signed-in user who can edit the project
  const generateText = async (
    input: { shortDescription: string; projectInstanceId?: string | null },
  ): Promise<GeneratedText> => {
    setTextError(null);
    setIsGeneratingText(true);
    try {
      if (!input.projectInstanceId) throw new Error('metadata suggestions need a project');
      const { data, error } = await toResult(v1.POST('/api/v1/metadata-suggestions', {
        body: { projectUniqueId: input.projectInstanceId, description: input.shortDescription },
      }));
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('usePublishAiAssist.generateText', err);
      setTextError(GENERIC_ERROR);
      return {};
    } finally {
      setIsGeneratingText(false);
    }
  };

  const generateImages = async (
    input: { seedUrl: string; projectInstanceId?: string | null },
  ): Promise<GeneratedImages> => {
    setImagesError(null);
    setIsGeneratingImages(true);
    try {
      if (!input.projectInstanceId) throw new Error('cover generation needs a project');
      // The client id is the job's: a repeat answers with the same job and generates nothing more
      const { data: job, error } = await toResult(v1.POST('/api/v1/cover-jobs', {
        body: { uniqueId: crypto.randomUUID(), projectUniqueId: input.projectInstanceId, seedImageUrl: input.seedUrl },
      }));
      if (error) throw error;

      const deadline = Date.now() + COVERS_POLL_TIMEOUT_MS;
      while (Date.now() < deadline) {
        await sleep(COVERS_POLL_INTERVAL_MS);
        const { data: current, error: pollError } = await toResult(v1.GET('/api/v1/cover-jobs/{jobUniqueId}', { params: { path: { jobUniqueId: job.uniqueId } } }));
        if (pollError) throw pollError;
        if (current.status === 'done') return { portraitUrl: current.portraitUrl ?? undefined, landscapeUrl: current.landscapeUrl ?? undefined };
        if (current.status === 'failed') throw new Error('cover generation failed');
      }
      throw new Error('cover generation timed out');
    } catch (err) {
      console.error('usePublishAiAssist.generateImages', err);
      setImagesError(GENERIC_ERROR);
      return {};
    } finally {
      setIsGeneratingImages(false);
    }
  };

  return {
    isGeneratingText,
    isGeneratingImages,
    textError,
    imagesError,
    generateText,
    generateImages,
  };
}
